import type { MapNodeKindName, MapNodeSettings, MapRootName, MasterDataModel, MasterDataUpdateModel } from "@fortex/api";
import { parseSettings, resolveKind } from "@fortex/map";
import type { CatalogueKind } from "@fortex/map";

export type AdminKind = MapNodeKindName;

export type IdNameProblem = "required" | "prefix" | "chars" | "taken";

export const rootPrefix: Record<MapRootName, string> = { MAP: "MAP_", LAYER: "LAYER_" };

export const allowedKinds: Record<MapRootName, AdminKind[]> = {
  MAP: ["Group", "Tile", "Special", "Folder", "GeoObject"],
  LAYER: ["Group", "Folder", "GeoObject"]
};

const kindNames: Record<CatalogueKind, AdminKind> = { group: "Group", tile: "Tile", special: "Special", folder: "Folder", geoobject: "GeoObject" };

export function kindOf(value: string | null, hasChildren: boolean): { kind: AdminKind; known: boolean } {
  const resolved = resolveKind(value, hasChildren);

  return { kind: kindNames[resolved.kind], known: resolved.known };
}

export function mapRootOf(idName: string): MapRootName | null {
  const upper = idName.trim().toUpperCase();

  if (upper.startsWith(rootPrefix.MAP)) return "MAP";
  if (upper.startsWith(rootPrefix.LAYER)) return "LAYER";

  return null;
}

export function settingsOf(row: Pick<MasterDataModel, "value2">): MapNodeSettings {
  return parseSettings(row.value2).settings;
}

export function idNameProblem(idName: string, root: MapRootName | null, taken: (candidate: string) => boolean): IdNameProblem | null {
  const trimmed = idName.trim();

  if (trimmed.length === 0) return "required";
  if (root !== null && (!trimmed.startsWith(rootPrefix[root]) || trimmed.length === rootPrefix[root].length)) return "prefix";
  if (!/^[A-Z0-9_]+$/.test(trimmed)) return "chars";
  if (taken(trimmed)) return "taken";

  return null;
}

export function defaultSettings(kind: AdminKind, root: MapRootName): MapNodeSettings {
  switch (kind) {
    case "Group":
      return { selection: "multi", collapsed: false };
    case "Tile":
      return { urlTemplate: "", subdomains: "abc", attribution: "", minZoom: 0, maxZoom: 19, maxNativeZoom: 19, tileSize: 256, detectRetina: false, dark: false };
    case "Special":
      return { provider: "google", providerOptions: {} };
    case "Folder":
      return { folder: "", layer: null, minZoom: null, maxZoom: null };
    case "GeoObject":
      return root === "LAYER" ? { includeDescendants: false, cluster: true, labels: false, simplify: true, maxFeatures: 5000 } : { includeDescendants: false, cluster: true, labels: false };
  }
}

const commonKeys: (keyof MapNodeSettings)[] = ["minZoom", "maxZoom", "opacity", "defaultOn", "attribution", "bounds", "legend", "thumbnail"];

export function switchKind(previous: MapNodeSettings, kind: AdminKind, root: MapRootName): MapNodeSettings {
  const next: MapNodeSettings = { ...defaultSettings(kind, root) };

  for (const key of commonKeys) {
    if (previous[key] !== undefined && previous[key] !== null) next[key] = previous[key];
  }

  return next;
}

export function settingsJson(settings: MapNodeSettings): string {
  return JSON.stringify(settings, null, 2);
}

export function newRow(root: MapRootName | null, parent: MasterDataModel | null, rootId: number | null): MasterDataModel {
  return {
    id: 0,
    idName: root === null ? "" : rootPrefix[root],
    dataId: 0,
    order_: 0,
    value: root === null ? "" : "Group",
    value2: root === null ? "" : settingsJson(defaultSettings("Group", root)),
    visible: true,
    parentMasterData_Id: parent?.id ?? rootId,
    parentIdName: parent?.idName ?? (root ?? null),
    name: { lang: ["", ""] },
    masterDataIcon: null,
    hasIcon: false,
    attr: null
  };
}

export function toUpdateModel(row: MasterDataModel): MasterDataUpdateModel {
  return {
    idName: row.idName.trim(),
    dataId: row.dataId,
    order_: row.order_,
    value: row.value,
    value2: row.value2,
    visible: row.visible,
    parentMasterData_Id: row.parentMasterData_Id,
    parentIdName: row.parentIdName,
    name: row.name,
    masterDataIcon: row.masterDataIcon,
    attr: row.attr,
    extraDataTypeList: row.extraDataTypeList ?? []
  };
}

export type MasterDataIndex = { byId: Map<number, MasterDataModel>; childCount: Map<number, number> };

export function indexRows(rows: MasterDataModel[]): MasterDataIndex {
  const byId = new Map(rows.map(a => [a.id, a] as const));
  const childCount = new Map<number, number>();

  for (const row of rows) {
    if (row.parentMasterData_Id === null) continue;

    childCount.set(row.parentMasterData_Id, (childCount.get(row.parentMasterData_Id) ?? 0) + 1);
  }

  return { byId, childCount };
}

export function pathNamesOf(row: MasterDataModel, index: MasterDataIndex, nameOf: (row: MasterDataModel) => string): string[] {
  const names: string[] = [];
  let current = row.parentMasterData_Id === null ? undefined : index.byId.get(row.parentMasterData_Id);
  let guard = 0;

  while (current !== undefined && guard < 64) {
    names.unshift(nameOf(current));
    current = current.parentMasterData_Id === null ? undefined : index.byId.get(current.parentMasterData_Id);
    guard++;
  }

  return names;
}

export function masterDataField(langId: number): (row: MasterDataModel, field: string) => unknown {
  return (row, field) => {
    switch (field) {
      case "Id":
        return row.id;
      case "IdName":
        return row.idName;
      case "Value":
        return row.value;
      case "Value2":
        return row.value2;
      case "DataId":
        return row.dataId;
      case "Order_":
        return row.order_;
      case "Visible":
        return row.visible;
      case "ParentMasterData.IdName":
        return row.parentIdName;
      default: {
        const match = /^Name\.Lang(\d)Short$/.exec(field);

        if (match !== null) return row.name?.lang?.[Number(match[1]) - 1] ?? "";

        return row.name?.lang?.[langId - 1] ?? "";
      }
    }
  };
}
